interface MaskValueParams {
  value: string;
  visibleStart?: number;
  visibleEnd?: number;
  maskChar?: string;
  fixedMaskLength?: number;
}

export const maskValue = ({
  value,
  visibleStart = 2,
  visibleEnd = 2,
  maskChar = '*',
  fixedMaskLength = 6
}: MaskValueParams): string => {
  if (!value) return value;
  const len = value.length;
  if (len <= visibleStart + visibleEnd) return maskChar.repeat(fixedMaskLength);
  return (
    value.slice(0, visibleStart) + maskChar.repeat(fixedMaskLength) + value.slice(len - visibleEnd)
  );
};

interface SecretPattern {
  name: string;
  pattern: RegExp;
  /**
   * Cheap substring checks used to decide whether this regex should run.
   * MUST be O(n) simple includes() checks.
   */
  hints: readonly string[];
  replacer: (groups: string[], mask: (params: MaskValueParams) => string) => string;
}

/** Maximum length for a single secret/token value */
const MAX_SECRET_LEN = 16_384;

/** Maximum input length to process */
const MAX_INPUT_LEN = 1_000_000;

/** Maximum whitespace to match (prevents backtracking on whitespace) */
const MAX_WHITESPACE = 100;

/** Maximum token length for auth headers */
const MAX_TOKEN_LEN = 8_192;

/** Known secret key names */
const SECRET_KEYS = 'NG_API_AUTHTOKEN|API_KEY|SECRET_KEY';

const SECRET_PATTERNS: readonly SecretPattern[] = [
  /**
   * HTTP Authorization header with ApiKey or Bearer token
   * @example "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..."
   * @example "Authorization: ApiKey sk-1234567890abcdef"
   */
  {
    name: 'auth-header',
    hints: ['Authorization'],
    pattern: new RegExp(
      String.raw`(Authorization:\s{0,${MAX_WHITESPACE}}(?:ApiKey|Bearer)\s{1,${MAX_WHITESPACE}})(\S{1,${MAX_TOKEN_LEN}})`,
      'gi'
    ),
    replacer: ([, pre, token], mask) => pre + mask({ value: token })
  },

  /**
   * Quoted Authorization attribute (common in config files, curl commands)
   * @example 'Authorization="ApiKey sk-1234567890abcdef"'
   * @example 'Authorization="Bearer eyJhbGciOiJIUzI1NiIs..."'
   */
  {
    name: 'auth-header-quoted',
    hints: ['Authorization'],
    pattern: new RegExp(
      String.raw`(Authorization\s{0,${MAX_WHITESPACE}}=\s{0,${MAX_WHITESPACE}}"(?:ApiKey|Bearer)\s{1,${MAX_WHITESPACE}})([^"]{1,${MAX_TOKEN_LEN}})(")`,
      'gi'
    ),
    replacer: ([, pre, token, post], mask) => pre + mask({ value: token }) + post
  },

  /**
   * Query string token parameter
   * @example "https://api.example.com/data?token=abc123xyz"
   * @example "https://api.example.com/data?foo=bar&token=abc123xyz"
   */
  {
    name: 'query-token',
    hints: ['token='],
    pattern: new RegExp(String.raw`([?&]token=)([^&\s"'\`]{1,${MAX_TOKEN_LEN}})`, 'gi'),
    replacer: ([, pre, token], mask) => pre + mask({ value: token })
  },

  /**
   * JSON key-value pairs for known secret keys
   * @example '"NG_API_AUTHTOKEN": "sk-1234567890abcdef"'
   * @example '"API_KEY": "abc123"'
   * @example '"SECRET_KEY": "super-secret-value"'
   */
  {
    name: 'json-secret',
    hints: ['API_KEY', 'SECRET_KEY', 'NG_API_AUTHTOKEN'],
    pattern: new RegExp(
      String.raw`("(?:${SECRET_KEYS})"\s{0,${MAX_WHITESPACE}}:\s{0,${MAX_WHITESPACE}}")([^"]{0,${MAX_SECRET_LEN}})(")`,
      'gi'
    ),
    replacer: ([, pre, token, post], mask) => pre + mask({ value: token }) + post
  },

  /**
   * Environment variable assignments (.env files, shell exports)
   * @example "NG_API_AUTHTOKEN=sk-1234567890abcdef"
   * @example "API_KEY=abc123"
   * @example " SECRET_KEY = super-secret-value"
   */
  {
    name: 'env-secret',
    hints: ['API_KEY', 'SECRET_KEY', 'NG_API_AUTHTOKEN'],
    pattern: new RegExp(
      String.raw`(^|[\r\n])(\s{0,${MAX_WHITESPACE}}(?:${SECRET_KEYS})\s{0,${MAX_WHITESPACE}}=\s{0,${MAX_WHITESPACE}})([^\r\n]{1,${MAX_SECRET_LEN}})`,
      'gi'
    ),
    replacer: ([, line, key, token], mask) => line + key + mask({ value: token })
  }
];

/**
 * Replaces sensitive values in strings with masked versions.
 * Useful for sanitizing logs, error messages, and debug output.
 *
 * DoS hardening:
 * - Input length cap (1MB)
 * - Secret length caps (16KB per secret)
 * - Bounded whitespace matching
 * - No backtracking-prone alternations
 * - Only executes regexes whose hint substring is present
 *
 * @param input - The string potentially containing secrets
 * @param mask - Optional custom mask function (defaults to maskValue)
 * @returns The input string with all detected secrets masked
 *
 * @example
 * scrambleSecrets('Authorization: Bearer eyJhbGci...')
 * // → 'Authorization: Bearer ey******ci'
 *
 * @example
 * scrambleSecrets('API_KEY=sk-12345678', () => '[REDACTED]')
 * // → 'API_KEY=[REDACTED]'
 */
export const scrambleSecrets = (
  input: string,
  mask: (params: MaskValueParams) => string = maskValue
): string => {
  if (!input) return input;

  // Truncate oversized input (don't throw - log sanitization shouldn't crash)
  if (input.length > MAX_INPUT_LEN) {
    input = input.slice(0, MAX_INPUT_LEN);
  }

  let result = input;

  for (const { hints, pattern, replacer } of SECRET_PATTERNS) {
    // Skip regex if no hint substring is present (O(n) check is cheaper than regex)
    if (!hints.some(h => result.includes(h))) continue;

    // Reset lastIndex for global regex reuse
    pattern.lastIndex = 0;

    result = result.replace(pattern, (...args) => replacer(args.slice(0, -2) as string[], mask));
  }

  return result;
};
